import React from 'react';
import { useTranslation } from 'react-i18next';

const HAAT_LOGO_URL =
  'https://cdn.prod.website-files.com/6829acf870093a50cc6ceb69/682b03419ead6e45d8f2c59e_logo%20(2).svg';

interface FooterProps {
  /** When true, renders the footer with a dark palette (e.g. on the landing page). */
  dark?: boolean;
}

interface FooterLink {
  labelKey: string;
  href: string;
}

interface FooterColumn {
  titleKey: string;
  links: FooterLink[];
}

/**
 * Multi-column marketing footer (structure mirrors the Wolt merchant footer).
 * Labels resolve through i18n while URLs remain stable across languages.
 */
const FOOTER_COLUMNS: FooterColumn[] = [
  {
    titleKey: 'footer.columns.partnerWithHaat',
    links: [
      { labelKey: 'footer.links.forCouriers', href: 'https://courier-connect.haat.delivery' },
    ],
  },
  {
    titleKey: 'footer.columns.company',
    links: [
      { labelKey: 'footer.links.aboutUs', href: 'https://haat.delivery/' },
      { labelKey: 'footer.links.jobs', href: 'https://careers.haat.delivery/' },
    ],
  },
  {
    titleKey: 'footer.columns.usefulLinks',
    links: [
      { labelKey: 'footer.links.newsroom', href: 'https://haat.delivery/newsroom' },
      { labelKey: 'footer.links.contact', href: 'https://haat.delivery/contact-us' },
    ],
  },
  {
    titleKey: 'footer.columns.followUs',
    links: [
      { labelKey: 'footer.links.instagram', href: 'https://www.instagram.com/haat.delivery/' },
      { labelKey: 'footer.links.facebook', href: 'https://www.facebook.com/HAATAPP/' },
      { labelKey: 'footer.links.tiktok', href: 'https://www.tiktok.com/@haat.delivery?lang=en' },
      { labelKey: 'footer.links.linkedin', href: 'https://www.linkedin.com/company/haat-delivery/posts/?feedView=all' },
      { labelKey: 'footer.links.youtube', href: 'https://www.youtube.com/channel/UCl5olLUQ17Sw064XGhoslxA' },
    ],
  },
];

const LEGAL_LINKS: FooterLink[] = [
  { labelKey: 'footer.legal.accessibilityStatement', href: 'https://haat.delivery/accessibility-statement' },
  { labelKey: 'footer.legal.termsOfService', href: 'https://haat.delivery/terms-and-conditions' },
  { labelKey: 'footer.legal.privacyStatement', href: 'https://haat.delivery/privacy-policy' },
];

const Footer: React.FC<FooterProps> = ({ dark = false }) => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  const headingClass = dark ? 'text-white' : 'text-gray-900';
  const linkClass = dark
    ? 'text-gray-400 hover:text-white'
    : 'text-gray-500 hover:text-gray-900';
  const dividerClass = dark ? 'border-white/10' : 'border-gray-200';

  return (
    <footer
      className={`border-t mt-auto ${dark ? 'border-white/10 bg-[#1b1b1f]' : 'border-gray-200/60 bg-white'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Brand */}
        <div className="mb-10">
          <img
            src={HAAT_LOGO_URL}
            alt={t('appName') ?? 'HAAT'}
            className="h-9 w-auto"
            loading="lazy"
            decoding="async"
          />
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-5">
          {FOOTER_COLUMNS.map((column) => (
            <nav key={column.titleKey} aria-label={t(column.titleKey) as string}>
              <h3
                className={`text-sm font-semibold mb-4 ${headingClass}`}
              >
                {t(column.titleKey)}
              </h3>
              <ul className="space-y-3">
                {column.links.map((link) => {
                  const isExternal = link.href.startsWith('http');
                  return (
                    <li key={link.labelKey}>
                      <a
                        href={link.href}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                        className={`text-sm transition-colors ${linkClass}`}
                      >
                        {t(link.labelKey)}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>
          ))}
        </div>

        {/* Bottom bar */}
        <div
          className={`mt-12 pt-8 border-t ${dividerClass} flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between`}
        >
          <p className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-500'}`}>
            {t('footer.copyright', { year })}
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {LEGAL_LINKS.map((link) => {
              const isExternal = link.href.startsWith('http');
              return (
                <a
                  key={link.labelKey}
                  href={link.href}
                  target={isExternal ? '_blank' : undefined}
                  rel={isExternal ? 'noopener noreferrer' : undefined}
                  className={`text-xs transition-colors ${linkClass}`}
                >
                  {t(link.labelKey)}
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
